import { Loader2, Lock } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

/**
 * THE PRODUCT'S ONE SHARED CONFIRMATION BOX — ADM-02 §8, which is the only
 * place in the specification that describes it in full, and describes it
 * precisely because a build must reuse it rather than invent another.
 *
 * GEN-204, GEN-205, GEN-206, GEN-207, GEN-208 in one component: every
 * irreversible act opens THIS dialog; title-question + body + the verb on
 * the action button — never OK/Yes/Confirm alone; backdrop click and Escape
 * close and do nothing (the native <dialog>'s cancel); nothing happens until
 * the action button is clicked and nothing auto-dismisses. GEN-209's
 * roster (draft deletes, cancels, count posts…) are its callers, and
 * GEN-210's amount-naming variants supply their own body text. The
 * typed-reason siblings (GEN-59's cancel dialogs) are deliberately separate.
 *
 * §8, quoted, because every line below is one of these sentences:
 *
 *   "It sits above everything else, so it can appear on top of another
 *    dialog." → a native `<dialog>` opened with `showModal()`, which puts it in
 *    the browser's own top layer. Not a z-index, which is a number somebody
 *    else can beat.
 *   "It is 420 pixels wide, with a dark blurred backdrop. CLICKING OUTSIDE
 *    CANCELS."
 *   "Its icon is a PADLOCK ON AN AMBER TILE, not a warning triangle — because
 *    these actions make something PERMANENT rather than merely dangerous."
 *   "The title and the body are supplied by whatever raised it."
 *   "The confirm button carries THE VERB, NEVER THE WORD OK."
 *
 * ── THE SPLIT §8 ASKS A BUILD TO KEEP ─────────────────────────────────────
 *
 * "An act that is irreversible but needs no explanation gets this shared box.
 * An act that needs a TYPED REASON gets its own dialog with a required text
 * box instead." So this component has no text field and must not grow one:
 * ownership transfer, tenant deletion and a rejected correction each keep
 * their own typed-confirmation dialog, and the difference between the two
 * shapes is the difference between "are you sure" and "say why".
 *
 * ── ITS CALLERS, AND THE SENTENCE THAT USED TO CAP THEM AT THREE ──────────
 *
 *   - `features/admin/integrations-page.tsx`  — revoke a key (BR-ADM-02-03)
 *   - `features/projects/timesheets-page.tsx` — approve a week (BR-PRJ-03-07)
 *   - `features/hr/attendance-page.tsx`       — approve a correction (AP-4)
 *   - twenty-five deletes, through `DeleteConfirmBox` below
 *     (the twenty-fourth is MD-14's unused-tier delete, BR-MD-14-08; the
 *     twenty-fifth is IT-N4's soft delete on the item editor)
 *
 * The middle two arrived late and each failed §8 in its own way: the
 * attendance approval called `window.confirm`, which is browser chrome with an
 * OK button (§8 twice over), and the timesheet approval raised a bordered
 * panel of its own — a fourth pattern, which BR-PERM-15 forbids outright ("a
 * new pattern must not be invented"). Both are swaps to this component now.
 *
 * ── WHY THE DELETES ARRIVED, AND WHY THE OLD COMMENT HERE WAS WRONG ───────
 *
 * This block used to end: "A FOURTH caller is a spec question, not a coding
 * decision: 16.5.3 names three… The `window.confirm` calls elsewhere in the
 * product guard ordinary deletes and cancels, which 16.5.3 does not reach."
 * It is preserved here because it cited GEN-209 four lines above itself and
 * then reasoned its way out of following it, and a comment that argues against
 * the rule it cites is worth leaving visible rather than quietly deleting.
 *
 * The sentence it leaned on is §16.5.3 prose, spec line 35160: "It is used in
 * EXACTLY THREE PLACES." Eight lines below it, in the same section, sits that
 * section's own RULE — BR-PERM-15, spec line 35168: "Every irreversible action
 * must use one of these two patterns. A new pattern must not be invented."
 * A section that disagrees with itself is settled by its rule, not by its
 * description of the prototype. GEN-209 (spec line 4709) then says outright
 * which actions, and DELETING A DRAFT IS FIRST ON ITS LIST of seven.
 *
 * And the conflict never had to be resolved for the old state to be wrong:
 * `window.confirm` is NEITHER of the two permitted patterns, so all twenty
 * delete sites breached BR-PERM-15 under either reading of the prose.
 *
 * SPEC-VERSUS-BUILD DEVIATION, recorded here so nobody has to rediscover it.
 * §16.5.3's sentence at spec line 35160 — "it is used in exactly three places:
 * approving a timesheet week, approving an attendance correction, and revoking
 * an API key" — no longer describes this product. The build has dozens of
 * callers (29 deletes alone, plus the confirm and typed-reason sites), and it
 * has them BECAUSE of GEN-209 (spec line 4709) and BR-PERM-15
 * (spec line 35168), not in spite of them. The owner should be able to find out
 * that one sentence in their specification now describes something the build
 * deliberately does not do; this is that record.
 *
 * §8's OTHER pattern — "its own dialog with a required text box" — is
 * `reason-box.tsx`, which the GEN-72 posted-document cancels and the
 * BR-FIN-03-17/BR-FIN-04-11 voucher reversals raise. It composes this shell
 * (the required text box is caller-supplied BODY content), so this component
 * still has no text field of its own and §8's geometry lives in one place.
 * Those flows previously called `window.prompt` — browser chrome, the same
 * §8 breach as the `window.confirm` above.
 *
 * ── THE THIRD OUTCOME, AND WHY IT IS AN EXTENSION AND NOT A SECOND BOX ────
 *
 * ERR-ADM-06-04 (spec line 32061) is the first caller whose answer is not
 * yes-or-no. Its remedy column says "Save or discard", and its sentence —
 * "You changed permissions on Accountant. Save them before switching?" — makes
 * three answers meaningful: save the edits and go, throw them away and go, or
 * go nowhere. A two-button box cannot express that, and the obvious escape is
 * to write a second component.
 *
 * THAT IS THE ONE THING NOT TO DO. GEN-58's whole achievement was putting one
 * mandated sentence in ONE place across twenty-six delete sites; BR-PERM-15
 * (spec line 35168) says outright that "a new pattern must not be invented".
 * A `RolesUnsavedBox` would satisfy neither. So the shared box grows an
 * OPTIONAL third outcome instead, and the twenty-six existing callers pass
 * nothing and render exactly what they rendered before — two buttons, same
 * variants, same order. That is asserted rather than assumed, in
 * `confirm-box-three-outcomes.test.tsx`.
 *
 * The pair is a UNION, not two independent optionals, so a caller physically
 * cannot supply a label with no handler (a button that does nothing) or a
 * handler with no label (a path nobody can reach). The compiler carries that,
 * not a runtime check and not a convention.
 *
 * ── WHAT ESCAPE AND THE BACKDROP DO WHEN THERE ARE THREE ANSWERS ──────────
 *
 * Unchanged, and GEN-207 (spec line 4706) is why: "Clicking the backdrop or
 * pressing Escape must close the dialog and DO NOTHING." With two outcomes
 * "do nothing" and "cancel" were the same event, so the distinction never had
 * to be drawn. With three they could have come apart — and the rule already
 * settles it. Escape, the backdrop and Cancel are all `onCancel`, all three
 * mean "no", and NONE of them may be routed to the secondary action however
 * convenient that looks: the secondary is an act, and GEN-208 says nothing
 * happens until a button is clicked.
 *
 * The secondary IS disabled while `busy`, and Cancel still is not. That split
 * is deliberate: Cancel is the way out of a modal and must never be taken away
 * (see `confirm-box-busy.test.tsx`), whereas the secondary is a second action
 * and letting it race the first is how a user gets both.
 */
export function ConfirmBox({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
  secondaryLabel,
  onSecondary,
  busy,
}: {
  open: boolean;
  title: string;
  /**
   * §8: "The title and the body are supplied by whatever raised it."
   *
   * A NODE and not a string, because one caller has content a sentence cannot
   * hold: ADM-02's new key is a SECRET shown once, and it needs to be its own
   * addressable, selectable, LTR-pinned element rather than a run of
   * characters inside a paragraph of prose. A string caller passes a string
   * and nothing changes for it.
   */
  body: ReactNode;
  /** §8: the VERB. "Revoke", "Disconnect", "Approve" — never "OK". */
  confirmLabel: string;
  onConfirm: () => void;
  /** §8's "Clicking outside cancels", GEN-207's Escape, and the Cancel button.
   *  All three, always — "do nothing" is one outcome however many acts there
   *  are. */
  onCancel: () => void;
  busy?: boolean;
} & (
  | { secondaryLabel?: undefined; onSecondary?: undefined }
  | {
      /** ERR-ADM-06-04's second act — "Discard". A VERB like `confirmLabel`,
       *  under GEN-206, and never "No": the two acts must be told apart by
       *  what they do, not by which one is the negative. */
      secondaryLabel: string;
      onSecondary: () => void;
    }
)) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // `showModal` rather than an `open` attribute: it is what puts the element
    // in the TOP LAYER — §8's "sits above everything else, so it can appear on
    // top of another dialog" — and it traps focus and makes Escape work with no
    // handler of ours.
    //
    // The fallback is not defensive padding: jsdom implements <dialog> without
    // `showModal`, so a component that assumed it would make every screen test
    // that ever opens a confirmation throw. Falling back to the `open`
    // attribute renders the same markup with the same content and only loses
    // the top layer, which is exactly the property a test does not measure.
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
      // §8: "Clicking outside cancels." The backdrop is the dialog element
      // itself; a click whose target IS the dialog landed outside the panel.
      onClick={(event) => {
        if (event.target === ref.current) onCancel();
      }}
      aria-labelledby="confirm-box-title"
      // `m-auto` puts back the UA centring that Tailwind v4 preflight
      // (`* { margin: 0 }`) strips from <dialog> — without it every confirm
      // box opened in the top-left corner (bugs: Accounting Periods).
      className="m-auto w-[420px] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card p-0 text-foreground backdrop:bg-black/50 backdrop:backdrop-blur-sm"
    >
      <div className="grid gap-3 p-6">
        {/* §8: a PADLOCK on an amber tile, not a warning triangle. The
            distinction is the message: this is permanent, not risky. */}
        <span
          className="flex size-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400"
          aria-hidden
        >
          <Lock className="size-5" />
        </span>
        <h2 id="confirm-box-title" className="text-base font-bold">
          {title}
        </h2>
        <div className="text-sm text-muted-foreground">{body}</div>
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {/*
            The third outcome, when there is one. It sits BETWEEN Cancel and
            the confirm button so the row still reads left-to-right as
            "no · the other act · the act", and so the confirm button stays
            last — every existing caller's verb is the final control in the
            dialog and a caller that adds a secondary must not move it.

            `variant="outline"`, and that is a deliberate NON-decision. The
            confirm button's `destructive` is untouched here, which is the
            whole point: twenty-six callers render identically. Which of the
            three ought to be red on the roles screen is a live colour
            question (owner decision 20 has the palette open, with the dark
            theme's delete red measured at 2.89 against a 4.5 bar), and this
            wave is not entitled to answer it. Outline adds no ink/background
            pair that is not already measured. Recorded as a follow-up rather
            than guessed at — and note jsdom renders no colour anyway, so no
            test below could have caught a wrong guess.
          */}
          {secondaryLabel === undefined ? null : (
            <Button
              type="button"
              variant="outline"
              onClick={onSecondary}
              disabled={busy === true}
            >
              {secondaryLabel}
            </Button>
          )}
          {/*
            GEN-60 and GEN-161, both of which ask for TWO things: greyed out
            AND a spinner inside the button until the work finishes. This box
            did only the grey, which is the half that says least — a button
            that dims without moving looks unavailable rather than busy, and
            that is what makes somebody reload the page mid-revoke.

            The spinner is the document editors' one, unchanged and not
            reinvented: `animate-spin` beside the verb, `aria-hidden` because
            §8's verb is already the button's accessible name. The label
            stays — a spinner that REPLACED it would satisfy GEN-161 by
            breaking §8's "the confirm button carries the verb".
          */}
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={busy === true}>
            {busy === true ? <Loader2 className="animate-spin" aria-hidden /> : null}
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * GEN-58's DELETE CONFIRMATION, IN ONE PLACE — spec line 3804: "Delete must
 * always ask 'Delete this permanently? This cannot be undone.' and must never
 * appear on a confirmed record."
 *
 * ── WHY THE MANDATED SENTENCE IS SPLIT ACROSS TITLE AND BODY ──────────────
 *
 * Not a compromise — it is what GEN-205 asks for: "The dialog must carry four
 * things: a TITLE THAT ASKS A QUESTION, a BODY that says what will happen AND
 * THAT IT CANNOT BE UNDONE, a Cancel button, and an action button labelled
 * with the verb." GEN-58's sentence is a question followed by a statement, and
 * those are exactly GEN-205's two slots:
 *
 *     title  →  "Delete this permanently?"     common.confirmDeleteTitle
 *     body   →  "This cannot be undone."       common.confirmDeleteUndone
 *     verb   →  "Delete"                       common.delete  (GEN-206: never OK)
 *
 * Reading the open dialog top to bottom gives GEN-58's sentence back, whole.
 * It lives HERE and not in twenty dictionary strings so that there is one
 * place for it to be right, and one place for a test to read.
 *
 * ── `detail` IS NOT DECORATION, AND FLATTENING IT WOULD BREAK TWO RULES ───
 *
 * GEN-58 says delete must always ASK that sentence. It does not say the box
 * may say nothing else, and two other rules require that it does:
 *
 *   GEN-205  the body must say WHAT WILL HAPPEN — which "this cannot be
 *            undone" does not, on its own, for any particular record.
 *   GEN-210  "An action that destroys money or stock data must additionally
 *            NAME THE AMOUNT OR THE COUNT in the body."
 *
 * So the twenty `*.confirmDelete` strings are kept and passed in here. Some of
 * them carry information the mandated sentence cannot — the group's unit count,
 * that nothing has entered stock, that nothing has left the warehouse, which
 * company owns nothing. Replacing those with one sentence would have been a
 * regression that looked like rule compliance, and a GEN-210 breach besides.
 *
 * Three of the twenty used to END with "This cannot be undone." themselves
 * (`wh`, `adj`, `trf`). That half-sentence is now this component's job, so it
 * was removed from those three rather than rendered twice — asserted absent in
 * `gen58-delete-confirmation.test.tsx`, because "restoring" it would read as a
 * fidelity improvement and produce a doubled sentence on three screens.
 */
export function DeleteConfirmBox({
  open,
  detail,
  onConfirm,
  onCancel,
  busy,
}: {
  open: boolean;
  /** The screen's own "what will happen" sentence — GEN-205, and GEN-210's
   *  amount or count where money or stock dies. Optional: a screen with
   *  nothing further to say passes nothing and still asks GEN-58's sentence. */
  detail?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmBox
      open={open}
      title={t('common.confirmDeleteTitle')}
      body={
        detail == null ? (
          t('common.confirmDeleteUndone')
        ) : (
          <>
            {detail} {t('common.confirmDeleteUndone')}
          </>
        )
      }
      confirmLabel={t('common.delete')}
      onConfirm={onConfirm}
      onCancel={onCancel}
      // Narrowed rather than forwarded: `exactOptionalPropertyTypes` refuses a
      // `boolean | undefined` for an optional `boolean`, and ConfirmBox already
      // reads this as `busy === true`, so the two are the same value.
      busy={busy === true}
    />
  );
}
